/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../buttons/Button";
import { errorAlert, makeApiRequest, successAlert } from "../../util";
import AuthContext from "../../store/authContext";
import { ADD_MCPTT_USER_FORM_FIELDS } from "../../constants";
import Form from "../../ui/Form/Form";
import TextField from "../inputs/TextField";
import { IMCPTTUserData } from "../../types";
import DeleteModal, { DeleteModalIcons } from "../modal/DeleteModal";
import { MCUsersAdd } from "../../api/icCheck";
import {
  CREATE_MCPTT_USER_TRANSLATIONS,
  FORM_VALIDATION_TRANSLATIONS,
} from "../../translations/util";
import { ADD_MCPTT_USER_MAPPED_FIELDS } from "../../constants/addFormFields";
import DropdownField from "../inputs/DropdownField";
import ToggleField from "../inputs/ToggleField";

interface IProps {
  org: string;
}
const set = new Set();

const isFormInvalid = (
  { data }: { data: any },
  currentItem: string | string[]
) => {
  if (!data) return true;
  let isFieldEmpty = true;
  if (
    !currentItem.includes("+") &&
    (data["MAPPED-P25-ID"] !== "" ||
      data["MAPPED-DMR-ID"] !== "" ||
      data["MAPPED-ROIP-ID"] !== "" ||
      data["MAPPED-DFSI-ID"] !== "")
  ) {
    isFieldEmpty = false;
  } else if (
    currentItem.includes("+") &&
    data["MAPPED-P25-ID"] !== "" &&
    data["MAPPED-DFSI-ID"] !== ""
  ) {
    isFieldEmpty = false;
  }
  const isConnectionFormInvalid = ADD_MCPTT_USER_FORM_FIELDS.flatMap(
    (rows: { fields: any[] }) =>
      rows.fields.filter(
        (obj) => obj.type !== "DROPDOWN" && obj.type !== "TOGGLE"
      )
  )
    .map((obj: any) => obj.isInputInvalid(data[obj.name]))
    .reduce((acc: any, el: any) => {
      return acc || el || isFieldEmpty;
    }, isFieldEmpty);

  return isConnectionFormInvalid;
};

const NewMCPTTUser = ({ org }: IProps) => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    set.clear();
    return () => {
      set.clear();
    };
  }, []);

  const [currentItem, setCurrentItem] = useState<string>("");
  const [connectionData, setConnectionData] = useState<IMCPTTUserData>({
    "MCPTT-ID": "",
    "DISPLAY-NAME": "",
    "MAPPED-P25-ID": "",
    "MAPPED-DMR-ID": "",
    "MAPPED-ROIP-ID": "",
    "MAPPED-DFSI-ID": "",
    "MUTUAL-AID": false,
  });

  const createMCPTTUser = async () => {
    setLoading(true);
    try {
      await MCUsersAdd({
        data: JSON.stringify({
          "MCPTT-ID": connectionData["MCPTT-ID"],
          ...(connectionData &&
            connectionData["MAPPED-P25-ID"] && {
              "MAPPED-P25-ID": connectionData["MAPPED-P25-ID"],
            }),
          ...(connectionData &&
            connectionData["MAPPED-DMR-ID"] && {
              "MAPPED-DMR-ID": connectionData["MAPPED-DMR-ID"],
            }),
          ...(connectionData &&
            connectionData["MAPPED-ROIP-ID"] && {
              "MAPPED-ROIP-ID": connectionData["MAPPED-ROIP-ID"],
            }),
          ...(connectionData &&
            connectionData["MAPPED-DFSI-ID"] && {
              "MAPPED-DFSI-ID": connectionData["MAPPED-DFSI-ID"],
            }),
        }),
        org,
        token,
      });
      await makeApiRequest<{ Ref: string }>({
        path: `/api/v1/mc-users`,
        method: "POST",
        body: JSON.stringify({
          "MCPTT-ID": connectionData["MCPTT-ID"],
          "DISPLAY-NAME": connectionData["DISPLAY-NAME"],
          ...(connectionData &&
            connectionData["MAPPED-P25-ID"] && {
              "MAPPED-P25-ID": connectionData["MAPPED-P25-ID"],
            }),
          ...(connectionData &&
            connectionData["MAPPED-DMR-ID"] && {
              "MAPPED-DMR-ID": connectionData["MAPPED-DMR-ID"],
            }),
          ...(connectionData &&
            connectionData["MAPPED-ROIP-ID"] && {
              "MAPPED-ROIP-ID": connectionData["MAPPED-ROIP-ID"],
            }),
          ...(connectionData &&
            connectionData["MAPPED-DFSI-ID"] && {
              "MAPPED-DFSI-ID": connectionData["MAPPED-DFSI-ID"],
            }),
          ...(connectionData &&
            connectionData["MUTUAL-AID"] !== undefined &&
            [true, false].includes(connectionData["MUTUAL-AID"]) && {
              "MUTUAL-AID": connectionData["MUTUAL-AID"],
            }),
        }),
        token,
        org,
      });
      successAlert(CREATE_MCPTT_USER_TRANSLATIONS.successAlertMessage);
      navigate("/configurationManagement/mcpttUsers");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="data-form">
        <Form>
          <div className="data-tabcontent data-card">
            {ADD_MCPTT_USER_FORM_FIELDS.map(
              (rows: {
                cols: number;
                fields: {
                  name: keyof IMCPTTUserData;
                  tooltipText?: string;
                  label: string;
                  invalidText?: string | null;
                  isInputInvalid?: (
                    val?: string | string[],
                    data?: any
                  ) => boolean;
                  required?: boolean;
                  type: string;
                  items: string[];
                  placeholder: string[];
                  dropdownDisabled: boolean;
                }[];
              }) => {
                return (
                  <div
                    className={`grid grid-cols-${rows.cols} gap-4 form-control single-item`}
                  >
                    {rows.fields.map((field) => {
                      switch (field.type) {
                        case "TOGGLE":
                          return (
                            <ToggleField
                              labelText={field.label}
                              onChange={(val) =>
                                setConnectionData({
                                  ...connectionData,
                                  [field.name]: val,
                                })
                              }
                              value={
                                connectionData[field.name] as unknown as boolean
                              }
                              tooltipText={field.tooltipText}
                            />
                          );
                        case "DROPDOWN":
                          return (
                            <>
                              <DropdownField
                                className="dropdown-field"
                                label={field.label}
                                selectedItem={currentItem}
                                items={field.items}
                                onChange={({
                                  selectedItem,
                                }: {
                                  selectedItem: string;
                                }) => {
                                  if (currentItem.includes("+")) {
                                    const result = currentItem.split("+");
                                    setConnectionData({
                                      ...connectionData,
                                      [result[0].trim()]: "",
                                      [result[1].trim()]: "",
                                    });
                                  } else {
                                    setConnectionData({
                                      ...connectionData,
                                      [currentItem]: "",
                                    });
                                  }
                                  setCurrentItem(selectedItem);
                                }}
                                required
                                tooltipText={field.tooltipText}
                              />
                              {currentItem
                                ? currentItem
                                    .split("+")
                                    .map((mappedField: string) =>
                                      ADD_MCPTT_USER_MAPPED_FIELDS.filter(
                                        (field: {
                                          name: keyof IMCPTTUserData;
                                          tooltipText?: string;
                                          label: string;
                                          invalidText?: string | null;
                                          isInputInvalid?: (
                                            val?: string | string[],
                                            data?: any
                                          ) => boolean;
                                        }) => field.name === mappedField.trim()
                                      )
                                    )
                                    .flat(1)
                                    .map(
                                      (field: {
                                        name: keyof IMCPTTUserData;
                                        tooltipText?: string;
                                        label: string;
                                        invalidText?: string | null;
                                        isInputInvalid?: (
                                          val?: string | string[],
                                          data?: any
                                        ) => boolean;
                                      }) => {
                                        return (
                                          <TextField
                                            onChange={(val) => {
                                              set.add(field.name);
                                              setConnectionData({
                                                ...connectionData,
                                                [field.name]: val,
                                              });
                                            }}
                                            placeholder={field?.label}
                                            value={
                                              connectionData[
                                                field.name
                                              ] as string
                                            }
                                            invalid={
                                              set.has(field.name) &&
                                              field.isInputInvalid?.(
                                                connectionData[
                                                  field.name
                                                ] as string
                                              )
                                            }
                                            invalidText={
                                              field.invalidText ||
                                              FORM_VALIDATION_TRANSLATIONS.required(
                                                field.label
                                              )
                                            }
                                            labelText={field?.label}
                                            tooltipText={field.tooltipText}
                                          />
                                        );
                                      }
                                    )
                                : null}
                            </>
                          );
                        default:
                          return (
                            <TextField
                              onChange={(val) => {
                                set.add(field.name);
                                setConnectionData({
                                  ...connectionData,
                                  [field.name]: val,
                                });
                              }}
                              placeholder={field.label}
                              value={connectionData[field.name] as string}
                              invalid={
                                set.has(field.name) &&
                                field.isInputInvalid?.(
                                  connectionData[field.name] as string
                                )
                              }
                              invalidText={
                                field.invalidText ||
                                FORM_VALIDATION_TRANSLATIONS.required(
                                  field.label
                                )
                              }
                              labelText={field.label}
                              tooltipText={field.tooltipText}
                              required={field.required}
                            />
                          );
                      }
                    })}
                  </div>
                );
              }
            )}
          </div>
        </Form>
      </div>
      <DeleteModal
        modalHeaderText={CREATE_MCPTT_USER_TRANSLATIONS.deleteModal.header}
        modalDescriptionText={
          CREATE_MCPTT_USER_TRANSLATIONS.deleteModal.description
        }
        onDelete={() => navigate("/configurationManagement/mcpttUsers")}
        open={open}
        setOpen={setOpen}
        confirmationButtonLabel={
          CREATE_MCPTT_USER_TRANSLATIONS.deleteModal.goBackButtonLabel
        }
        cancelIcon={DeleteModalIcons.Cancel}
      />
      <div className="flex justify-end form-footer">
        <div className="mr-2">
          <Button
            className="w-full"
            type="button"
            kind="tertiary"
            disabled={false}
            label={CREATE_MCPTT_USER_TRANSLATIONS.cancelButtonLabel}
            onClick={() => setOpen(true)}
            dataCy="cancelCreateMCUser-"
          />
        </div>

        <div>
          <Button
            className="w-full"
            type="button"
            loading={loading}
            disabled={isFormInvalid({ data: connectionData }, currentItem)}
            onClick={createMCPTTUser}
            label={CREATE_MCPTT_USER_TRANSLATIONS.createButtonLabel}
            dataCy="createMCUser-"
          />
        </div>
      </div>
    </>
  );
};

export default NewMCPTTUser;
